import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';
import { videos, VideoItem } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const currentCategory = categories.find(c => c.slug === activeSlug) || categories[0];

  const filteredVideos: VideoItem[] = useMemo(() => {
    let result = activeSlug === 'all' ? [...videos] : videos.filter(v => v.categorySlug === activeSlug);

    switch (sortBy) {
      case 'popular':
        result.sort((a, b) => b.views - a.views);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'latest':
      default:
        result.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        break;
    }
    return result;
  }, [activeSlug, sortBy]);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 玖玖视频`,
    description: currentCategory.description,
    url: `https://www.jiujiu-video.com/category/${activeSlug}`,
  };

  return (
    <Layout>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <div className="pt-20 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory.name },
            ]}
          />

          <div className="py-8 md:py-12">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              {currentCategory.name}
            </h1>
            <p className="text-foreground-500 text-sm md:text-base">
              {currentCategory.description} · 共{filteredVideos.length}个视频
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 border border-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-2 sm:ml-auto">
              <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
              <div className="flex bg-background-100 rounded-full p-1">
                {([
                  { key: 'latest', label: '最新' },
                  { key: 'popular', label: '最热' },
                  { key: 'rating', label: '评分' },
                ] as const).map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      sortBy === opt.key
                        ? 'bg-background-50 text-primary-600 shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 pb-16" data-product-shop>
            {filteredVideos.map(video => (
              <Link
                key={video.id}
                to={`/video/${video.id}`}
                className="group cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-lg mb-3 aspect-video bg-background-200">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    title={`${video.title} - 玖玖视频`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/90 text-primary-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <i className="ri-play-fill text-2xl"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-2 right-2 bg-black/75 text-background-50 text-xs px-2 py-0.5 rounded font-medium">
                    {video.duration}
                  </div>
                  {video.isNew && (
                    <div className="absolute top-2 left-2">
                      <span className="bg-accent-500/90 text-background-50 text-xs px-2 py-0.5 rounded font-medium">最新</span>
                    </div>
                  )}
                  {video.isHot && !video.isNew && (
                    <div className="absolute top-2 left-2">
                      <span className="bg-primary-500/90 text-background-50 text-xs px-2 py-0.5 rounded font-medium">热门</span>
                    </div>
                  )}
                </div>
                <h3 className="text-sm font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                  {video.title}
                </h3>
                <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-eye-line"></i>
                    </div>
                    {video.views >= 10000 ? `${(video.views / 10000).toFixed(1)}万` : video.views}
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500 text-xs"></i>
                    </div>
                    {video.rating}
                  </span>
                  <span className="text-foreground-400">{video.date}</span>
                </div>
              </Link>
            ))}
          </div>

          {filteredVideos.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-video-line text-2xl"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无视频内容</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}